import { useState } from "react";
import { PackageCheck, Plus, Trash2, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Recepción en CEDIS sobre una Orden de Compra: captura lo recibido e
// incrementa el inventario (mod_retail_inventory + producto maestro).
export default function RetailCedisReception({ item, onRefresh }) {
  const [ubic, setUbic] = useState(item.cedis || "");
  const [lineas, setLineas] = useState([{ sku: "", nombre: "", cantidad: "" }]);
  const [parcial, setParcial] = useState(false);
  const [busy, setBusy] = useState(false);
  const recepciones = item.recepciones || [];

  const setLine = (i, k, v) => setLineas((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLineas((ls) => [...ls, { sku: "", nombre: "", cantidad: "" }]);
  const rmLine = (i) => setLineas((ls) => ls.filter((_, idx) => idx !== i));

  const receive = async () => {
    const valid = lineas.filter((l) => l.sku.trim() && Number(l.cantidad) > 0);
    if (!valid.length) { toast.error("Captura al menos una línea con SKU y cantidad"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/retail/purchase-orders/${item.id}/receive`, {
        ubicacion: ubic || undefined, parcial,
        lineas: valid.map((l) => ({ sku: l.sku.trim(), nombre: l.nombre.trim(), cantidad: Number(l.cantidad) })),
      });
      toast.success(`Recibidas ${data.recibido} pzs en ${data.ubicacion} · inventario actualizado`);
      setLineas([{ sku: "", nombre: "", cantidad: "" }]);
      onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid="cedis-reception">
      <div className="flex items-center gap-2 overline text-navy/40 mb-3"><PackageCheck className="w-3.5 h-3.5" /> Recepción en CEDIS</div>

      <div className="flex items-center gap-2 mb-2">
        <Truck className="w-4 h-4 text-navy/40" />
        <input value={ubic} onChange={(e) => setUbic(e.target.value)} placeholder="CEDIS / Bodega de recepción" className="flex-1 field-sm" data-testid="cedis-ubicacion" />
      </div>

      <div className="space-y-2 mb-2">
        {lineas.map((l, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`cedis-line-${i}`}>
            <input value={l.sku} onChange={(e) => setLine(i, "sku", e.target.value)} placeholder="SKU" className="w-28 field-sm" data-testid={`cedis-sku-${i}`} />
            <input value={l.nombre} onChange={(e) => setLine(i, "nombre", e.target.value)} placeholder="Descripción (opcional)" className="flex-1 field-sm" />
            <input type="number" min="0" value={l.cantidad} onChange={(e) => setLine(i, "cantidad", e.target.value)} placeholder="Cant." className="w-20 field-sm" data-testid={`cedis-qty-${i}`} />
            <button onClick={() => rmLine(i)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={addLine} className="btn btn-ghost text-sm" data-testid="cedis-add-line"><Plus className="w-3.5 h-3.5" /> Agregar línea</button>
      </div>

      <label className="flex items-center gap-2 text-sm text-navy/60 mb-3">
        <input type="checkbox" checked={parcial} onChange={(e) => setParcial(e.target.checked)} data-testid="cedis-parcial" /> Recepción parcial (la orden sigue abierta)
      </label>

      <button onClick={receive} disabled={busy} className="btn btn-primary text-sm w-full" data-testid="cedis-receive">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Confirmar recepción e ingresar a inventario
      </button>

      {recepciones.length > 0 && (
        <div className="mt-4">
          <div className="overline text-navy/40 mb-2">Historial de recepciones</div>
          <div className="space-y-1.5">
            {recepciones.map((r, i) => (
              <div key={i} className="text-xs bg-cream border border-navy/10 rounded-lg px-3 py-2 flex justify-between" data-testid={`cedis-history-${i}`}>
                <span>{new Date(r.at).toLocaleString("es-MX")} · {r.ubicacion}{r.parcial ? " · parcial" : ""}</span>
                <span className="font-medium">{r.piezas} pzs</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
